import { gql } from '@apollo/client'
import { logDataDogError } from '../../../../../errors/utils/errors'
import { FetchApiClient } from '../fetch-api-client'
import { GraphQLApiClient } from '../graphql/client'
import { CacheFactory } from '../ui/services/cache'
import { INTROSPECTION_QUERY } from './query'
import {
  addExtraFieldsToFieldDefinitions,
  pickValidFieldsFromIntrospectionQuery,
  transformFieldListToObjectMap,
} from './utils'

export interface SchemaResponse {
  name: string
  mapping: string
  label: string
  isCustom?: boolean
}

/**
 * ElasticORM field type (`custom/metadata/:mapping`). Richer than the
 * protobuf `DataField_Kind` — distinguishes `url` / `email` / `phone` /
 * `textarea` / `picklist` / `multipicklist` / `reference` etc.
 *
 * `''` is the backend's "unspecified / unknown type" — consumers should
 * treat it as an opaque string field (plain text input, no special handling).
 */
export type ResourceSchemaFieldType =
  | 'string'
  | 'id'
  | 'calculated'
  | 'email'
  | 'encryptedstring'
  | 'reference'
  | 'textarea'
  | 'htmltextarea'
  | 'combobox'
  | 'multipicklist'
  | 'picklist'
  | 'phone'
  | 'url'
  | 'int'
  | 'double'
  | 'decimal'
  | 'currency'
  | 'percent'
  | 'duration'
  | 'time'
  | 'date'
  | 'datetime'
  | 'boolean'
  | ''

export type ResourceSchemaFieldAccessMode =
  | 'read_only'
  | 'read_write'
  | 'insert_only'
  | 'update_only'

export interface ResourceSchemaPicklistValue {
  label: string
  value: string
  active: boolean
  defaultValue: boolean
}

export interface ResourceSchemaFieldRelationship {
  /** Local FK field mapping (e.g. `RegionId`). */
  mapping: string
  /** Related object's schema mapping (e.g. `Regions`). */
  schemaMapping: string
}

/**
 * A single field of a resource's ElasticORM schema. `nillable === false`
 * means the field is required on insert.
 *
 * `values` (picklist options) and `relationship` (the per-field lookup
 * target — present on `reference` fields) are optional: the endpoint omits
 * them for fields they don't apply to rather than guaranteeing `[]`/null, so
 * read sites must default (`field.values ?? []`). The per-field
 * `relationship` is the lookup the field *is*; the object-level
 * `ResourceSchemaMetadata.relationships` is the full relationship catalogue
 * (incl. reverse / has-many), keyed by `fieldMapping`.
 */
export interface ResourceSchemaField {
  name?: string
  mapping: string
  label: string
  type: ResourceSchemaFieldType
  accessMode: ResourceSchemaFieldAccessMode
  nillable: boolean
  unique: boolean
  defaultValue?: string | null
  maxLength?: number
  precision?: number
  scale?: number
  values?: ResourceSchemaPicklistValue[]
  relationship?: ResourceSchemaFieldRelationship
}

/**
 * Effective schema for a single resource, returned by
 * `GET custom/metadata/:mapping`. Includes every standard and custom field
 * with its ElasticORM `type`, nullability, picklist values and lookup
 * relationship — the metadata schema-driven forms need. `relationships` is
 * the object-level relationship catalogue (optional; omitted when none).
 */
export interface ResourceSchemaMetadata {
  name: string
  label: string
  labelPlural?: string
  mapping: string
  fields: ResourceSchemaField[]
  relationships?: Array<{
    mapping: string
    schemaMapping: string
    fieldMapping: string
  }>
}

export interface FieldDefinition {
  name: string
  kind?: string
  definer?: string
  searchByFields?: string[]
  fields?: Array<{
    name: string
    type: {
      name?: string
      ofType?: any
    }
  }>
}

export interface IntrospectionResponse {
  __schema: {
    types: FieldDefinition[]
    queryType?: { name: string }
    mutationType?: { name: string }
    subscriptionType?: { name: string }
  }
}

const GRAPHQL_DATA_ACCESS_CACHE_NAME = 'GraphqlDataAccessFields'
const CUSTOM_FIELDS_SCHEMA_CACHE_NAME = 'CustomFieldsSchema'
const RESOURCE_SCHEMA_METADATA_CACHE_NAME = 'ResourceSchemaMetadata'

export const GraphqlDataAccessCache = CacheFactory.create<
  null,
  IntrospectionResponse
>(GRAPHQL_DATA_ACCESS_CACHE_NAME)

export const CustomFieldsSchemaCache = CacheFactory.create<
  null,
  SchemaResponse[]
>(CUSTOM_FIELDS_SCHEMA_CACHE_NAME)

export const ResourceSchemaMetadataCache = CacheFactory.create<
  string,
  ResourceSchemaMetadata
>(RESOURCE_SCHEMA_METADATA_CACHE_NAME)

export class DataSchemaApiClient {
  private readonly graphqlClient: GraphQLApiClient
  private readonly fetchClient: FetchApiClient

  constructor(graphqlClient: GraphQLApiClient, fetchClient: FetchApiClient) {
    this.graphqlClient = graphqlClient
    this.fetchClient = fetchClient
  }

  async queryIntrospection(): Promise<IntrospectionResponse> {
    const fetchIntrospectionQuery = async () => {
      const result = await this.graphqlClient.fetch<IntrospectionResponse>({
        query: gql(INTROSPECTION_QUERY),
      })
      if (result.error || !result.data) {
        throw (
          result.error ?? new Error('Introspection query returned null data')
        )
      }
      return result.data
    }
    try {
      return await GraphqlDataAccessCache.execute(null, fetchIntrospectionQuery)
    } catch (error) {
      logDataDogError(error as Error, {
        component: 'DataSchemaApiClient',
        description: 'Failed to fetch GraphQL introspection query',
      })
      return {
        __schema: {
          types: [],
        },
      }
    }
  }

  async getSchemaObjects(): Promise<SchemaResponse[]> {
    const fetchSchemaObjects = async () => {
      const standardSchemas = await this.fetchClient.get<SchemaResponse[]>(
        'custom/standard_schemas',
      )
      const customSchemas =
        await this.fetchClient.get<SchemaResponse[]>('custom/schemas')
      return [
        ...standardSchemas,
        ...customSchemas.map((schema) => ({ ...schema, isCustom: true })),
      ]
    }

    return CustomFieldsSchemaCache.execute(null, fetchSchemaObjects)
  }

  /**
   * Effective schema for one resource — every standard and custom field with
   * its ElasticORM `type`, nullability, picklist values and lookup
   * relationship. `mapping` is the object's schema mapping (resolve a
   * resource name to it via `getSchemaObjects()`). Cached per mapping.
   */
  async getSchemaFields(mapping: string): Promise<ResourceSchemaMetadata> {
    return ResourceSchemaMetadataCache.execute(mapping, () =>
      this.fetchClient.get<ResourceSchemaMetadata>(
        `custom/metadata/${encodeURIComponent(mapping)}`,
      ),
    )
  }

  async fetchFieldDefinitions(): Promise<{ [key: string]: FieldDefinition }> {
    try {
      const [allowedSchemas, introspectionResponse] = await Promise.all([
        this.getSchemaObjects(),
        this.queryIntrospection(),
      ])

      const fieldDefinitions = pickValidFieldsFromIntrospectionQuery(
        allowedSchemas,
        introspectionResponse.__schema.types,
      )

      return transformFieldListToObjectMap(
        addExtraFieldsToFieldDefinitions(fieldDefinitions),
      )
    } catch (error) {
      logDataDogError(error as Error, {
        component: 'DataSchemaApiClient',
        description: 'Failed to fetch field definitions',
      })
      return transformFieldListToObjectMap(addExtraFieldsToFieldDefinitions([]))
    }
  }
}
